import { inject, Injectable } from '@angular/core'
import { WatchListAssetInterface } from '@modules/watch-list-module/interfaces/watch-list-asset.interface'
import { WatchListResponseInterface } from '@modules/watch-list-module/interfaces/watch-list-response.interface'
import { BehaviorSubject } from 'rxjs'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'

@Injectable({
	providedIn: 'root',
})
export class WatchListHelperService {
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum

	public watchList$: BehaviorSubject<WatchListResponseInterface | null> =
		new BehaviorSubject<WatchListResponseInterface | null>(null)
	public isLoadingWatchList$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	get isMarketOpen(): boolean {
		return this._marketStatusHelperService.isMarketOpen
	}

	public setWatchList(response: WatchListResponseInterface): void {
		if (!this.isMarketOpen) {
			this.watchList$.next(response)
			return
		}
		const assets: WatchListAssetInterface[] = response.assets.map(
			(asset: WatchListAssetInterface): WatchListAssetInterface => ({
				...asset,
				close: asset.last_trade_price,
				previous_price_change: asset.price_change,
				previous_percentage_change: asset.percentage_change,
			}),
		)
		this.watchList$.next({ ...response, assets })
	}

	public deleteAsset(symbol: string): WatchListResponseInterface | null {
		const watchList: WatchListResponseInterface | null = this.watchList$.getValue()
		if (!watchList) return null
		const assets: WatchListAssetInterface[] = watchList.assets.filter(
			(asset: WatchListAssetInterface): boolean => asset.symbol !== symbol,
		)
		return { ...watchList, assets }
	}

	public updateAssetsPrices(ticker: string, price: number, liveTicker: LiveTickerInterface | null): void {
		const watchList: WatchListResponseInterface | null = this.watchList$.getValue()
		const marketStatus: MarketStatusEnum = this.marketStatus
		if (!marketStatus || !watchList?.assets?.length) return

		const updatedAssets: WatchListAssetInterface[] = watchList.assets.map(
			(asset: WatchListAssetInterface): WatchListAssetInterface => {
				if (asset.symbol !== ticker) return asset

				if (liveTicker && asset.symbol === liveTicker.ticker) {
					return {
						...asset,
						...(!this.isMarketOpen
							? {
									last_trade_price: liveTicker.marketStatusPrices?.price ?? 0,
									price_change: liveTicker.marketStatusPrices?.changeMoney ?? 0,
									percentage_change: liveTicker.marketStatusPrices?.changePercent ?? 0,
								}
							: {
									close: liveTicker.currentPrices.price ?? 0,
									previous_price_change: liveTicker.currentPrices.changeMoney ?? 0,
									previous_percentage_change: liveTicker.currentPrices.changePercent ?? 0,
								}),
					}
				}

				let resPrice: number = 0
				let changeDollar: number = 0
				let changePercent: number = 0

				switch (marketStatus) {
					case this.marketStatusEnum.marketOpen:
						resPrice = price || asset.close
						changeDollar = resPrice - asset.prev_day
						changePercent = (changeDollar / asset.prev_day) * 100
						break
					case this.marketStatusEnum.preMarket:
					case this.marketStatusEnum.postMarket:
					case this.marketStatusEnum.marketClose:
						resPrice = price || asset.close
						changeDollar = resPrice - asset.close
						changePercent = (changeDollar / asset.close) * 100
						break
					default:
						return asset
				}

				return {
					...asset,
					...(!this.isMarketOpen
						? {
								last_trade_price: resPrice,
								price_change: changeDollar,
								percentage_change: changePercent,
							}
						: {
								close: resPrice,
								previous_price_change: changeDollar,
								previous_percentage_change: changePercent,
							}),
				}
			},
		)

		this.watchList$.next({
			...watchList,
			assets: updatedAssets,
		})
	}
}
